import { z } from 'zod';
import { PublicApiScope, ApiKeyType } from '@trulyinbox/types';
import { apiFetch } from './api';
import type { WorkspaceRole } from './settings';

/**
 * Request wrappers + Zod schemas for the internal (JWT-guarded) `/api-keys`
 * key-management surface that powers the Settings → API keys section. Mirrors
 * the shape of `lib/settings.ts`: one file per backend resource, exporting
 * typed request functions consumed by `features/settings/queries.ts`.
 *
 * The scope + type unions are DERIVED from the `@trulyinbox/types` enums via
 * `z.nativeEnum` — never re-declared as a parallel hand-written union.
 */

// ── Schemas ──────────────────────────────────────────────────────────────────

export const apiKeySchema = z.object({
  id: z.number(),
  name: z.string(),
  type: z.nativeEnum(ApiKeyType),
  scopes: z.array(z.nativeEnum(PublicApiScope)),
  keyPrefix: z.string(),
  workspaceId: z.number().nullable(),
  wsRole: z.enum(['admin', 'editor', 'viewer']).nullable(),
  createdBy: z.number(),
  createdAt: z.string(),
  lastUsedAt: z.string().nullable(),
  revokedAt: z.string().nullable(),
});

/** The create response additionally carries the one-time plaintext secret. */
export const createdApiKeySchema = apiKeySchema.extend({ key: z.string() });

export const apiKeyListSchema = z.array(apiKeySchema);

// ── Types ────────────────────────────────────────────────────────────────────

export type ApiKeyDto = z.infer<typeof apiKeySchema>;
export type CreatedApiKeyDto = z.infer<typeof createdApiKeySchema>;
export type ApiKeyScope = PublicApiScope;

export const PUBLIC_API_SCOPES = Object.values(PublicApiScope);

export type CreateApiKeyInput = {
  name: string;
  type: ApiKeyType;
  scopes: ApiKeyScope[];
  workspaceId?: number;
  wsRole?: WorkspaceRole;
};

// ── Requests ─────────────────────────────────────────────────────────────────

export async function listApiKeys(): Promise<ApiKeyDto[]> {
  const data = await apiFetch<unknown>('/api-keys');
  return apiKeyListSchema.parse(data);
}

export async function createApiKey(input: CreateApiKeyInput): Promise<CreatedApiKeyDto> {
  const data = await apiFetch<unknown>('/api-keys', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return createdApiKeySchema.parse(data);
}

export async function revokeApiKey(id: number): Promise<void> {
  await apiFetch<void>(`/api-keys/${id}`, { method: 'DELETE' });
}
